/* eslint-disable react/require-default-props */
import React from 'react';
import camelToCapitalize from '@utils/functions/camelToCapitalize';
import { translate } from '@utils/i18n';
import { IssueStatusEnum } from '../../types';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import DeviceInfoToggle from './DeviceInfoToggle';
import ManagerNote from './ManagerNote';
import IssueLayout from './IssueLayout';
import getLocalizedQuestionText from '../../utils/getLocalizedQuestionText';
import getCurrentLanguage from '../../utils/getCurrentLanguage';
import './styles.scss';
export default function UnansweredQuestionCard({ issue, defaultExpanded, onToggle, onDeviceInfoToggle, onUpdateIssue, onUndoAction, readOnly = false, settings }) {
    var _a, _b, _c, _d, _e, _f;
    const isHighImpact = issue.status !== IssueStatusEnum.Resolved && ((_a = issue.issueData) === null || _a === void 0 ? void 0 : _a.highImpact) === true;
    const userLanguage = getCurrentLanguage();
    const status = issue.status || IssueStatusEnum.Detected;
    // IP-14405 / IP-14406: multi-field answer records (e.g. an alarm device's
    // pass_fail / visual_check / functional_test) collapse into a single issue
    // that carries the full list of missing sub-fields. Render them together so
    // the office sees "N missing answers" and exactly which fields on one card.
    const missingFields = (_b = issue.issueData.missingFields) !== null && _b !== void 0 ? _b : [];
    const missingFieldCount = (_c = issue.issueData.missingFieldCount) !== null && _c !== void 0 ? _c : missingFields.length;
    // Handler functions for issue actions
    const handleSuggestCorrection = () => {
        onUpdateIssue(issue.id, status, true);
    };
    const handleDismissIssue = () => {
        onUpdateIssue(issue.id, IssueStatusEnum.DismissedByBackOffice, false);
    };
    const handleNoteChange = (note) => {
        onUpdateIssue(issue.id, status, issue.markedForSuggestion, note);
    };
    // Create high impact badge if needed
    const highImpactBadge = isHighImpact ? (React.createElement("span", { className: "issue-card__badge issue-card__badge--high-impact" }, translate('high_impact', { namespace: 'features.iim_review.issue_card' }))) : undefined;
    if (issue.issueData.isSummaryIssue) {
        return (React.createElement(IssueLayout, { issueType: "unanswered_question", badge: highImpactBadge, status: status, defaultExpanded: defaultExpanded, onToggle: onToggle, onSuggestCorrection: handleSuggestCorrection, onDismissIssue: handleDismissIssue, onUndoAction: onUndoAction, readOnly: readOnly, statusChanges: issue.statusChanges, markedForSuggestion: issue.markedForSuggestion },
            React.createElement("p", null, issue.description)));
    }
    return (React.createElement(IssueLayout, { issueType: "unanswered_question", badge: highImpactBadge, status: status, defaultExpanded: defaultExpanded, onToggle: onToggle, onSuggestCorrection: handleSuggestCorrection, onDismissIssue: handleDismissIssue, onUndoAction: onUndoAction, readOnly: readOnly, statusChanges: issue.statusChanges, markedForSuggestion: issue.markedForSuggestion },
        React.createElement(React.Fragment, null,
            React.createElement("p", null,
                React.createElement("b", null,
                    ((_d = issue.question) === null || _d === void 0 ? void 0 : _d.type) && camelToCapitalize(issue.question.type),
                    " /",
                    ' ',
                    humanizeInspectableType((_e = issue.inspectable) === null || _e === void 0 ? void 0 : _e.type)),
                React.createElement("br", null),
                React.createElement("span", null, getLocalizedQuestionText(issue.question, userLanguage))),
            missingFieldCount > 1 && missingFields.length > 0 && (React.createElement("div", { className: "issue-card__missing-fields" },
                React.createElement("p", { className: "issue-card__missing-fields-count" },
                    React.createElement("b", null, translate('missing_answers', {
                        namespace: 'features.iim_review.issue_card',
                        count: missingFieldCount
                    }))),
                React.createElement("ul", { className: "issue-card__missing-fields-list" }, missingFields.map((field) => (React.createElement("li", { key: field.key }, field.label)))))),
            React.createElement(DeviceInfoToggle, { inspectable: issue.inspectable, onToggle: onDeviceInfoToggle }),
            issue.markedForSuggestion && (!readOnly || ((_f = issue.managerNote) === null || _f === void 0 ? void 0 : _f.trim())) && (React.createElement(ManagerNote, { note: issue.managerNote || '', onNoteChange: handleNoteChange, disabled: readOnly })))));
}
